/**
 * Regroupement des tests ignorés par motif.
 *
 * Azure DevOps ne fournit pas de champ « raison du skip » : le motif arrive en texte
 * libre dans `errorMessage` (ce qu'écrit le SKIP Robot Framework) ou, à défaut, dans
 * `comment`. On ne peut donc pas s'appuyer sur une taxonomie fermée — les catégories
 * émergent des messages eux-mêmes.
 *
 * La normalisation sert uniquement à replier les messages quasi identiques sur une
 * même clé : sans elle, « Bug 12345 en cours » et « Bug 67890 en cours » compteraient
 * pour deux motifs distincts. Le libellé affiché reste le premier message réel
 * rencontré, jamais la clé normalisée.
 */

export const NO_REASON_KEY = '__sans_motif__';
export const NO_REASON_LABEL = 'Sans motif renseigné';

/** Longueur au-delà de laquelle un motif est tronqué à l'affichage. */
const MAX_LABEL_LENGTH = 160;

/**
 * Extrait le motif brut d'un résultat de test ADO.
 * `errorMessage` d'abord (le SKIP Robot Framework y écrit son message), `comment` en secours.
 */
export const rawSkipReason = (result) => {
  const candidates = [result?.errorMessage, result?.comment];
  for (const c of candidates) {
    const text = String(c ?? '').trim();
    if (text) return text;
  }
  return '';
};

/**
 * Clé de regroupement : minuscules, espaces repliés, et neutralisation des fragments
 * volatils (identifiants, dates, durées) qui feraient éclater un motif en N variantes.
 */
export const normalizeSkipReason = (raw) => {
  const text = String(raw ?? '').trim();
  if (!text) return NO_REASON_KEY;

  return text
    .toLowerCase()
    .replace(/\s+/g, ' ')
    // dates ISO ou FR, puis heures — un motif daté reste un seul motif
    .replace(/\d{4}-\d{2}-\d{2}([t ]\d{2}:\d{2}(:\d{2})?)?/g, '<date>')
    .replace(/\d{2}\/\d{2}\/\d{4}/g, '<date>')
    .replace(/\d{1,2}:\d{2}(:\d{2})?/g, '<heure>')
    // durées ("12.4s", "300 ms")
    .replace(/\d+([.,]\d+)?\s*(ms|s|sec|secondes?|min|minutes?)\b/g, '<durée>')
    // tout autre nombre : ids de bug, numéros de ticket, compteurs
    .replace(/\d+/g, '<n>')
    .replace(/\s+/g, ' ')
    .replace(/[.;,\s]+$/, '')
    .trim();
};

const truncate = (text) =>
  text.length > MAX_LABEL_LENGTH ? `${text.slice(0, MAX_LABEL_LENGTH - 1).trimEnd()}…` : text;

/**
 * Regroupe des résultats de tests ignorés par motif.
 *
 * @param {Array} results - résultats ignorés ({ testName, suiteName, squadName, errorMessage, comment })
 * @returns {Array} motifs triés par nombre décroissant :
 *   { key, label, count, isEmpty, tests: [{ testName, suiteName, squadName }] }
 */
export const groupSkipReasons = (results = []) => {
  const groups = new Map();

  for (const result of results) {
    const raw = rawSkipReason(result);
    const key = normalizeSkipReason(raw);

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: key === NO_REASON_KEY ? NO_REASON_LABEL : truncate(raw),
        count: 0,
        isEmpty: key === NO_REASON_KEY,
        tests: [],
      });
    }

    const group = groups.get(key);
    group.count += 1;
    group.tests.push({
      testName: result?.testName || result?.testCaseTitle || 'Test sans nom',
      suiteName: result?.suiteName || null,
      squadName: result?.squadName || null,
    });
  }

  // Les motifs les plus fréquents d'abord ; « sans motif » toujours en dernier,
  // c'est du bruit à traiter, pas une cause à analyser.
  return Array.from(groups.values()).sort((a, b) => {
    if (a.isEmpty !== b.isEmpty) return a.isEmpty ? 1 : -1;
    return b.count - a.count;
  });
};
